import { EMPTY_OBJ } from '@dadohr/shared/domainTypes/emptyConstants';
import {
  AutomationActionDenormalized,
  AutomationActionTemplate,
  AutomationActionType,
  ExternalServiceType,
  FileResourceType,
  IAutomationActionTemplateAtlassianCreateIssue,
  IAutomationActionTemplateCreateDocumentFromTemplate,
  IAutomationActionTemplateDoceboEnroll,
  IAutomationActionTemplateEnrollInExperience,
  IAutomationActionTemplateGoogleAddToGroup,
  IAutomationActionTemplateGoogleCalendarEventInvitation,
  IAutomationActionTemplateHttpPost,
  IAutomationActionTemplateMicrosoftAddToGroup,
  IAutomationActionTemplateMicrosoftCalendarEventInvitation,
  IAutomationActionTemplateMicrosoftInviteToChannel,
  IAutomationActionTemplateOktaAddToGroup,
  IAutomationActionTemplateSlackInviteToChannel,
  IAutomationActionTemplateSyncToDataDestination,
  IAutomationActionTemplateUnenrollFromExperience,
  IAutomationActionTemplateWorkdayTriggerBusinessProcess,
  PARTICIPANT_ACTOR_KEY,
  SubmitTargetType,
  WorkdayBusinessProcess
} from '@dadohr/shared/domainTypes/experience';
import { Integration } from '@dadohr/shared/domainTypes/integrations';
import { compact } from 'lodash';
import { useMemo } from 'react';
import shortid from 'shortid';
import { useUnsafeTranslation } from '../../../../../i18next';
import {
  useCurrentUser,
  useIsCurrentUserDadoEmployee
} from '../../../../../services/currentUser';
import { useIntegrationsContext } from '../../../../../services/integrations/context';
import { formatDatePrecise } from '../../../../../services/time';
import { useGoogleSheetsConfig } from '../../../../Org/pages/Integrations/integrations/googleSheets/service';
import { ActionDefinition, AutomationActionKey } from './types';

/*
@guide Create a new automation action - 2.1 Add defaults

Add defaults for your new type. This BlockDefiniton is responsible for making
your new item appear in the BlockSelector component when creating/editing an automation
in the admin app UI.
*/
export const useActionDefinitions = (): ActionDefinition[] => {
  const { t } = useUnsafeTranslation();
  const { active: activeIntegrations } = useIntegrationsContext();
  // This can be used within the unauthenticated experience preview, so be extra careful here
  const { id: userId, org } = useCurrentUser() || EMPTY_OBJ;
  const [googleConfig, loading, error] = useGoogleSheetsConfig(
    org?.id || 'NO_ORG',
    userId || 'NO_USER'
  );
  const isDadoEmployee = useIsCurrentUserDadoEmployee();

  return useMemo(() => {
    return compact([
      {
        key: AutomationActionKey.GOOGLE_ADD_TO_GROUP,
        type: AutomationActionType.GOOGLE_ADD_TO_GROUP,
        label: t('Google – Add to group'),
        shortLabel: t('Add to group'),
        iconSrc: '/images/integrations-logos/google_group.svg',
        emptyAction: (): IAutomationActionTemplateGoogleAddToGroup => ({
          id: shortid(),
          hooks: {},
          type: AutomationActionType.GOOGLE_ADD_TO_GROUP,
          group: {
            id: '',
            name: ''
          },
          pplToAdd: []
        }),
        enabled: activeIntegrations.has(Integration.GOOGLE_SHEETS),
        parentIntegration: Integration.GOOGLE_SHEETS
      },
      {
        key: AutomationActionKey.GOOGLE_CALENDAR_EVENT_INVITATION,
        type: AutomationActionType.GOOGLE_CALENDAR_EVENT_INVITATION,
        label: t('Google Calendar – Invite to event'),
        shortLabel: t('Invite to event'),
        iconSrc: '/images/integrations-logos/google_calendar_icon.svg',
        emptyAction: (): IAutomationActionTemplateGoogleCalendarEventInvitation => ({
          id: shortid(),
          hooks: {},
          type: AutomationActionType.GOOGLE_CALENDAR_EVENT_INVITATION,
          calendar: {
            id: '',
            name: ''
          },
          query: '',
          pplToAdd: [],
          anchor: {
            type: 'AFTER_AUTOMATION',
            lowerBoundary: 0
          },
          isRecurring: false,
          sendNoUpdateEmails: false
        }),
        enabled: activeIntegrations.has(Integration.GOOGLE_SHEETS),
        parentIntegration: Integration.GOOGLE_SHEETS
      },
      {
        key: AutomationActionKey.ENROLL_IN_EXPERIENCE,
        type: AutomationActionType.ENROLL_IN_EXPERIENCE,
        label: t('Enroll in Experience'),
        shortLabel: t('Enroll in Experience'),
        iconSrc: '/images/logo.svg',
        emptyAction: (): IAutomationActionTemplateEnrollInExperience => ({
          id: shortid(),
          hooks: {},
          type: AutomationActionType.ENROLL_IN_EXPERIENCE,
          actorToEnroll: PARTICIPANT_ACTOR_KEY,
          tplToEnrollIn: '',
          supportingActors: [],
          timing: { type: 'INSTANT' }
        }),
        enabled: true,
        parentIntegration: null
      },
      {
        key: AutomationActionKey.UNENROLL_FROM_EXPERIENCE,
        type: AutomationActionType.UNENROLL_FROM_EXPERIENCE,
        label: t('Unenroll from this Experience'),
        shortLabel: t('Unenroll from this Experience'),
        description: t(
          "The participant's experience will be archived immediately, and no further notifications will be sent."
        ),
        iconSrc: '/images/logo.svg',
        emptyAction: (): IAutomationActionTemplateUnenrollFromExperience => ({
          id: shortid(),
          hooks: {},
          type: AutomationActionType.UNENROLL_FROM_EXPERIENCE
        }),
        enabled: true,
        parentIntegration: null
      },
      {
        key: AutomationActionKey.SYNC_TO_DATA_DESTINATION,
        type: AutomationActionType.SYNC_TO_DATA_DESTINATION,
        label: t('Sync to Data Destination'),
        shortLabel: t('Sync to Data Destination'),
        description: t(
          'Sync data about the Participant to field(s) on their Dado user profile, or to an external system.'
        ),
        iconSrc: '/images/data_destination.svg',
        emptyAction: (): IAutomationActionTemplateSyncToDataDestination => ({
          id: shortid(),
          hooks: {},
          type: AutomationActionType.SYNC_TO_DATA_DESTINATION,
          // Default to a Google Sheet destination with no doc picked yet; the
          // admin changes it in the panel. Validation blocks an incomplete
          // destination / empty mappings.
          destination: {
            type: ExternalServiceType.GOOGLE_SHEET,
            resourceId: ''
          },
          mappings: []
        }),
        // The individual destinations are gated by active integrations inside the
        // settings panel, so the action itself is always offered.
        enabled: true,
        parentIntegration: null
      },
      {
        key: AutomationActionKey.WORKDAY_TRIGGER_BUSINESS_PROCESS,
        type: AutomationActionType.WORKDAY_TRIGGER_BUSINESS_PROCESS,
        label: t('Workday - Trigger business process'),
        shortLabel: t('Trigger business process'),
        description: t(
          'Update the Participant in Workday through a business process (start date, business title, work location, job profile, cost center, salary).'
        ),
        iconSrc: '/images/integrations-logos/workday.svg',
        emptyAction:
          (): IAutomationActionTemplateWorkdayTriggerBusinessProcess => ({
            id: shortid(),
            hooks: {},
            type: AutomationActionType.WORKDAY_TRIGGER_BUSINESS_PROCESS,
            process: WorkdayBusinessProcess.CORRECT_HIRE_EMPLOYEE,
            fieldMappings: [],
            settings: {},
            comment: null
          }),
        // Gated on the Workday write integration (SOAP/JWT). The RaaS read
        // integration is Integration.WORKDAY; writes use WORKDAY_API.
        enabled: activeIntegrations.has(Integration.WORKDAY_API),
        parentIntegration: Integration.WORKDAY_API
      },
      {
        key: AutomationActionKey.SLACK_INVITE_TO_CHANNEL,
        type: AutomationActionType.SLACK_INVITE_TO_CHANNEL,
        label: t('Slack - Invite to channel'),
        shortLabel: t('Invite to channel'),
        iconSrc: '/images/integrations-logos/slack_icon.svg',
        emptyAction: (): IAutomationActionTemplateSlackInviteToChannel => ({
          id: shortid(),
          hooks: {},
          type: AutomationActionType.SLACK_INVITE_TO_CHANNEL,
          pplToAdd: [],
          channel: {
            id: '',
            name: '',
            isPrivate: true,
            type: 'EXISTING'
          }
        }),
        enabled: activeIntegrations.has(Integration.SLACK),
        parentIntegration: Integration.SLACK
      },
      {
        key: AutomationActionKey.ASANA_CREATE_TASK,
        type: AutomationActionType.ASANA_CREATE_TASK,
        label: t('Asana - Create task'),
        shortLabel: 'Create task',
        iconSrc: '/images/integrations-logos/asana_icon.svg',
        emptyAction: () => ({
          id: shortid(),
          hooks: {},
          type: AutomationActionType.ASANA_CREATE_TASK,
          workspace: {
            id: '',
            name: ''
          },
          project: {
            id: '',
            name: '',
            color: ''
          },
          title: '',
          description: {
            t: '',
            v: 1
          },
          assignee: null,
          collaborators: [],
          duration: 0
        }),
        enabled: activeIntegrations.has(Integration.ASANA),
        parentIntegration: Integration.ASANA
      },
      {
        key: AutomationActionKey.ATLASSIAN_CREATE_ISSUE,
        type: AutomationActionType.ATLASSIAN_CREATE_ISSUE,
        label: t('Jira – Create issue'),
        shortLabel: t('Create issue'),
        iconSrc: '/images/integrations-logos/jira-icon.svg',
        emptyAction: (): IAutomationActionTemplateAtlassianCreateIssue => ({
          id: shortid(),
          hooks: {},
          type: AutomationActionType.ATLASSIAN_CREATE_ISSUE,
          project: { id: '', key: '', name: '' },
          issueType: { id: '', name: '', fields: [] },
          summary: '',
          description: '',
          reporter: null,
          fields: []
        }),
        enabled: activeIntegrations.has(Integration.ATLASSIAN),
        parentIntegration: Integration.ATLASSIAN
      },
      {
        key: AutomationActionKey.DOCEBO_ENROLL,
        type: AutomationActionType.DOCEBO_ENROLL,
        label: t('Docebo - Enroll in course'),
        shortLabel: t('Enroll in course'),
        iconSrc: '/images/integrations-logos/docebo-tiny-logo.svg',
        emptyAction: (): IAutomationActionTemplateDoceboEnroll => ({
          id: shortid(),
          hooks: {},
          type: AutomationActionType.DOCEBO_ENROLL,
          target: {
            id: 0,
            name: '',
            type: 'COURSE'
          },
          pplToAdd: []
        }),
        enabled: activeIntegrations.has(Integration.DOCEBO),
        parentIntegration: Integration.DOCEBO
      },
      {
        key: AutomationActionKey.LESSONLY_ASSIGN_TO_CONTENT,
        type: AutomationActionType.LESSONLY_ASSIGN_TO_CONTENT,
        label: t('Lessonly - Assign to Content'),
        shortLabel: 'Assign to Content',
        iconSrc: '/images/integrations-logos/lessonly-icon.webp',
        emptyAction: () => ({
          id: shortid(),
          hooks: {},
          type: AutomationActionType.LESSONLY_ASSIGN_TO_CONTENT,
          content: {
            id: 0,
            type: 'lesson',
            name: ''
          },
          pplToAdd: [],
          duration: null,
          assignWithDelayOf: 0,
          notify: true
        }),
        enabled: activeIntegrations.has(Integration.LESSONLY),
        parentIntegration: Integration.LESSONLY
      },
      {
        key: AutomationActionKey.MS_OUTLOOK_CALENDER_EVENT_INVITATION,
        type: AutomationActionType.MS_OUTLOOK_CALENDER_EVENT_INVITATION,
        label: t('Microsoft Outlook – Invite to event'),
        shortLabel: t('Invite to event'),
        iconSrc: '/images/integrations-logos/outlook-logo.svg',
        emptyAction: (): IAutomationActionTemplateMicrosoftCalendarEventInvitation => ({
          id: shortid(),
          hooks: {},
          type: AutomationActionType.MS_OUTLOOK_CALENDER_EVENT_INVITATION,
          calendar: {
            id: '',
            name: ''
          },
          eventId: '',
          query: '',
          pplToAdd: [],
          anchor: {
            type: 'AFTER_AUTOMATION',
            lowerBoundary: 0
          },
          isRecurring: false
        }),
        enabled: activeIntegrations.has(Integration.MICROSOFT),
        parentIntegration: Integration.MICROSOFT
      },
      {
        key: AutomationActionKey.MS_OUTLOOK_ADD_TO_GROUP,
        type: AutomationActionType.MS_OUTLOOK_ADD_TO_GROUP,
        label: t('Microsoft 365 – Add to group/team'),
        shortLabel: t('Add to group/team'),
        iconSrc: '/images/integrations-logos/microsoft/Microsoft-365-logo.svg',
        emptyAction: (): IAutomationActionTemplateMicrosoftAddToGroup => ({
          id: shortid(),
          hooks: {},
          type: AutomationActionType.MS_OUTLOOK_ADD_TO_GROUP,
          group: {
            id: '',
            name: ''
          },
          pplToAdd: []
        }),
        enabled: activeIntegrations.has(Integration.MICROSOFT),
        parentIntegration: Integration.MICROSOFT
      },
      {
        key: AutomationActionKey.MS_TEAMS_INVITE_TO_CHANNEL,
        type: AutomationActionType.MS_TEAMS_INVITE_TO_CHANNEL,
        label: t('Microsoft Teams – Invite to channel'),
        shortLabel: t('Invite to channel'),
        // TODO: replace with Teams logo
        iconSrc: '/images/integrations-logos/microsoft/Microsoft-365-logo.svg',
        emptyAction: (): IAutomationActionTemplateMicrosoftInviteToChannel => ({
          id: shortid(),
          hooks: {},
          type: AutomationActionType.MS_TEAMS_INVITE_TO_CHANNEL,
          team: {
            id: '',
            name: ''
          },
          channel: {
            type: 'NEW',
            kind: 'PRIVATE',
            nameSchema: ''
          },
          pplToAdd: []
        }),
        enabled: activeIntegrations.has(Integration.MICROSOFT),
        parentIntegration: Integration.MICROSOFT
      },
      {
        key: AutomationActionKey.OKTA_ADD_TO_GROUP,
        type: AutomationActionType.OKTA_ADD_TO_GROUP,
        label: t('Okta – Add to group'),
        shortLabel: t('Add to group'),
        iconSrc: '/images/integrations-logos/okta_aura.png',
        emptyAction: (): IAutomationActionTemplateOktaAddToGroup => ({
          id: shortid(),
          hooks: {},
          type: AutomationActionType.OKTA_ADD_TO_GROUP,
          group: {
            id: '',
            name: ''
          },
          pplToAdd: []
        }),
        enabled: activeIntegrations.has(Integration.OKTA_API),
        parentIntegration: Integration.OKTA_API
      },
      {
        key: AutomationActionKey.CREATE_DOCUMENT_FROM_TEMPLATE_MS,
        type: AutomationActionType.CREATE_DOCUMENT_FROM_TEMPLATE,
        label: t('Microsoft - Create Document'),
        shortLabel: t('Create Document'),
        iconSrc: '/images/docx_icon.svg',
        emptyAction: (): IAutomationActionTemplateCreateDocumentFromTemplate => ({
          id: shortid(),
          hooks: {},
          type: AutomationActionType.CREATE_DOCUMENT_FROM_TEMPLATE,
          fileNameFormat: '{{ participant.fullName }}',
          target: {
            type: SubmitTargetType.ONEDRIVE,
            resourceId: ''
          },
          srcTemplate: {
            type: FileResourceType.MS_ONE_DRIVE,
            publicUrl: ''
          },
          pplToShareWith: []
        }),
        enabled: activeIntegrations.has(Integration.MICROSOFT),
        parentIntegration: Integration.MICROSOFT
      },
      {
        key: AutomationActionKey.CREATE_DOCUMENT_FROM_TEMPLATE_GOOGLE,
        type: AutomationActionType.CREATE_DOCUMENT_FROM_TEMPLATE,
        label: t('Google - Create Document'),
        shortLabel: t('Create Document'),
        iconSrc: '/images/google-docs.svg',
        emptyAction: (): IAutomationActionTemplateCreateDocumentFromTemplate => ({
          id: shortid(),
          hooks: {},
          type: AutomationActionType.CREATE_DOCUMENT_FROM_TEMPLATE,
          fileNameFormat: '{{ participant.fullName }}',
          target: {
            type: SubmitTargetType.GOOGLE_DRIVE,
            resourceId: ''
          },
          srcTemplate: {
            type: FileResourceType.GOOGLE_DRIVE,
            resourceId: ''
          },
          pplToShareWith: []
        }),
        enabled:
          activeIntegrations.has(Integration.GOOGLE_SHEETS) &&
          !!googleConfig &&
          googleConfig.data.scope.includes('documents'),
        parentIntegration: Integration.GOOGLE_SHEETS
      },
      {
        key: AutomationActionKey.HTTP_POST,
        type: AutomationActionType.HTTP_POST,
        label: t('HTTP post request'),
        shortLabel: t('HTTP post request'),
        iconSrc: '/images/braces.svg',
        emptyAction: (): IAutomationActionTemplateHttpPost => ({
          id: shortid(),
          hooks: {},
          type: AutomationActionType.HTTP_POST,
          auth: { type: 'NO_AUTH' },
          requestUrl: '',
          jsonPayload: ''
        }),
        enabled: true,
        parentIntegration: null
      }
    ]);
  }, [t, activeIntegrations, isDadoEmployee]);
};

export const getActionDefKey = (
  action: AutomationActionTemplate | AutomationActionDenormalized
) => {
  switch (action.type) {
    case AutomationActionType.ASANA_CREATE_TASK:
      return AutomationActionKey.ASANA_CREATE_TASK;

    case AutomationActionType.ATLASSIAN_CREATE_ISSUE:
      return AutomationActionKey.ATLASSIAN_CREATE_ISSUE;

    case AutomationActionType.DOCEBO_ENROLL:
      return AutomationActionKey.DOCEBO_ENROLL;

    case AutomationActionType.ENROLL_IN_EXPERIENCE:
      return AutomationActionKey.ENROLL_IN_EXPERIENCE;

    case AutomationActionType.GOOGLE_ADD_TO_GROUP:
      return AutomationActionKey.GOOGLE_ADD_TO_GROUP;

    case AutomationActionType.GOOGLE_CALENDAR_EVENT_INVITATION:
      return AutomationActionKey.GOOGLE_CALENDAR_EVENT_INVITATION;

    case AutomationActionType.LESSONLY_ASSIGN_TO_CONTENT:
      return AutomationActionKey.LESSONLY_ASSIGN_TO_CONTENT;

    case AutomationActionType.MS_OUTLOOK_ADD_TO_GROUP:
      return AutomationActionKey.MS_OUTLOOK_ADD_TO_GROUP;

    case AutomationActionType.MS_OUTLOOK_CALENDER_EVENT_INVITATION:
      return AutomationActionKey.MS_OUTLOOK_CALENDER_EVENT_INVITATION;

    case AutomationActionType.OKTA_ADD_TO_GROUP:
      return AutomationActionKey.OKTA_ADD_TO_GROUP;
    case AutomationActionType.MS_TEAMS_INVITE_TO_CHANNEL:
      return AutomationActionKey.MS_TEAMS_INVITE_TO_CHANNEL;
    case AutomationActionType.SLACK_INVITE_TO_CHANNEL:
      return AutomationActionKey.SLACK_INVITE_TO_CHANNEL;

    case AutomationActionType.CREATE_DOCUMENT_FROM_TEMPLATE:
      // AI-generated actions can arrive with a null/missing srcTemplate.
      // Return undefined so the caller SKIPS this action (see the `if (!def)`
      // guards at the call sites) instead of crashing on `.type` here — and
      // instead of matching a def and rendering the CreateDocument editor,
      // which also dereferences srcTemplate unguarded and would crash next.
      if (!action.srcTemplate) return undefined;
      return action.srcTemplate.type === FileResourceType.GOOGLE_DRIVE
        ? AutomationActionKey.CREATE_DOCUMENT_FROM_TEMPLATE_GOOGLE
        : AutomationActionKey.CREATE_DOCUMENT_FROM_TEMPLATE_MS;
    case AutomationActionType.HTTP_POST:
      return AutomationActionKey.HTTP_POST;
    case AutomationActionType.UNENROLL_FROM_EXPERIENCE:
      return AutomationActionKey.UNENROLL_FROM_EXPERIENCE;
    case AutomationActionType.SYNC_TO_DATA_DESTINATION:
      return AutomationActionKey.SYNC_TO_DATA_DESTINATION;
    case AutomationActionType.WORKDAY_TRIGGER_BUSINESS_PROCESS:
      return AutomationActionKey.WORKDAY_TRIGGER_BUSINESS_PROCESS;
    default:
      // Unknown / unsupported action type (e.g. malformed AI output). Do NOT
      // throw (assertNever) — that takes down the whole Automation tab. Return
      // undefined so the caller skips it; the "ACTION DEFINITION NOT FOUND"
      // log at the call site records it for debugging.
      return undefined;
  }
};

export const useActionDefinition = (
  action: AutomationActionTemplate | AutomationActionDenormalized
) => {
  const defs = useActionDefinitions();
  const key = getActionDefKey(action);
  const def = defs.find((d) => d.key === key);
  if (!def) {
    throw new Error(`UNKNOWN_ACTION_TYPE - ${key}`);
  }
  return def;
};

export const getAdditionalActionInfoText = (
  action: AutomationActionDenormalized
) => {
  if (action.type === AutomationActionType.GOOGLE_CALENDAR_EVENT_INVITATION) {
    const eventTitle = `"${action.data?.eventName || action.query}"`;
    const eventDate = action.data?.eventDate
      ? formatDatePrecise(action?.data.eventDate)
      : null;

    return eventDate ? `${eventTitle} | ${eventDate}` : eventTitle;
  }

  if (action.type === AutomationActionType.GOOGLE_ADD_TO_GROUP) {
    return `"${action.group.name}"`;
  }

  if (action.type === AutomationActionType.SLACK_INVITE_TO_CHANNEL) {
    if (action.data?.channel) {
      return `Channel: ${action.data.channel.name}`;
    }

    if (action.errorData?.data?.channel?.name) {
      return action.errorData?.data?.channel?.name;
    }
    if (action?.channel) {
      return action.channel.type === 'EXISTING'
        ? `Channel: ${action.channel.name}`
        : `Channel: ${action.channel.nameSchema}`;
    }
    return '';
  }

  return '';
};

export const automationActionToText = (
  action: AutomationActionDenormalized,
  actionDefinitions: ActionDefinition[]
) => {
  const def = actionDefinitions.find((a) => a.type === action.type);
  if (!def) {
    return '';
  }
  return [def.label, getAdditionalActionInfoText(action)]
    .filter((t) => t)
    .join(' - ');
};
